import { type Component, ComponentType } from "@/types";
import { BaseCommandHandler, type CommandResult } from "../BaseCommandHandler";

/**
 * Command Handler for TimeTracker components.
 *
 * Handles commands for:
 * - Changing view mode (timer, timesheet, calendar, reports)
 * - Adding/removing projects
 * - Setting hourly rates
 * - Adding/removing clients
 * - Adding/removing tasks
 * - Configuring billable settings
 * - Setting time rounding
 * - Toggling tags and billable toggle visibility
 */
export class TimeTrackerCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Pattern: change view mode
    this.commandPatterns.set(
      /(?:set |change |switch )?(?:view |display )?(?:mode )?(?:to )?(timer|timesheet|calendar|reports?)/i,
      (match) => {
        const mode = match[1].toLowerCase().replace(/s$/, '');
        const modeMap: Record<string, string> = {
          'timer': 'timer', 'timesheet': 'timesheet',
          'calendar': 'calendar', 'report': 'reports',
        };
        return { properties: { viewMode: modeMap[mode] || 'timer' } };
      }
    );

    // Pattern: add project
    this.commandPatterns.set(
      /add (?:a )?(?:new )?project\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const name = match[1].trim();
        const existingProjects = Array.isArray(component.properties?.projects) ? component.properties.projects : [];
        return {
          properties: {
            projects: [
              ...existingProjects,
              { id: `proj-${Date.now()}`, name, color: '#3B82F6', tasks: [] }
            ]
          }
        };
      }
    );

    // Pattern: remove project
    this.commandPatterns.set(
      /remove (?:the )?project\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const name = match[1].trim().toLowerCase();
        const projects = (Array.isArray(component.properties?.projects) ? component.properties.projects : [])
          .filter((p: { name: string }) => p.name.toLowerCase() !== name);
        return { properties: { projects } };
      }
    );

    // Pattern: set rate
    this.commandPatterns.set(
      /(?:set |change )?(?:hourly )?rate (?:to )?\$?(\d+(?:\.\d{1,2})?)/i,
      (match) => {
        return { properties: { defaultRate: parseFloat(match[1]) } };
      }
    );

    // Pattern: add client
    this.commandPatterns.set(
      /add (?:a )?(?:new )?client\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const name = match[1].trim();
        const existingClients = Array.isArray(component.properties?.clients) ? component.properties.clients : [];
        return {
          properties: {
            clients: [
              ...existingClients,
              { id: `client-${Date.now()}`, name, projects: [] }
            ]
          }
        };
      }
    );

    // Pattern: remove client
    this.commandPatterns.set(
      /remove (?:the )?client\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const name = match[1].trim().toLowerCase();
        const clients = (Array.isArray(component.properties?.clients) ? component.properties.clients : [])
          .filter((c: { name: string }) => c.name.toLowerCase() !== name);
        return { properties: { clients } };
      }
    );

    // Pattern: add task
    this.commandPatterns.set(
      /add (?:a )?(?:new )?task\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const name = match[1].trim();
        const existingTasks = Array.isArray(component.properties?.tasks) ? component.properties.tasks : [];
        return {
          properties: {
            tasks: [
              ...existingTasks,
              { id: `task-${Date.now()}`, name, billable: true }
            ]
          }
        };
      }
    );

    // Pattern: set currency
    this.commandPatterns.set(
      /(?:set |change )?currency (?:to )?(USD|EUR|GBP|AUD|CAD|JPY|CHF)/i,
      (match) => {
        return { properties: { currency: match[1].toUpperCase() } };
      }
    );

    // Pattern: set time rounding
    this.commandPatterns.set(
      /(?:set |change )?(?:time )?rounding (?:to )?(\d+)\s*(?:min(?:ute)?s?)?/i,
      (match) => {
        return { properties: { timeRounding: parseInt(match[1], 10) } };
      }
    );

    // Pattern: toggle billable
    this.commandPatterns.set(
      /(?:set )?default\s*(?:to\s*)?(billable|non.?billable)/i,
      (match) => {
        return { properties: { defaultBillable: match[1].toLowerCase() === 'billable' } };
      }
    );

    // Pattern: show/hide billable toggle
    this.commandPatterns.set(
      /(show|hide) billable toggle/i,
      (match) => ({ properties: { showBillableToggle: match[1].toLowerCase() === 'show' } })
    );

    // Pattern: show/hide tags
    this.commandPatterns.set(
      /(show|hide) tags/i,
      (match) => ({ properties: { showTags: match[1].toLowerCase() === 'show' } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.TIME_TRACKER) return false;
    const keywords = [
      'project', 'client', 'task', 'rate', 'timer', 'timesheet',
      'calendar', 'report', 'billable', 'rounding', 'currency',
      'tags', 'view', 'mode', 'hours',
    ];
    return keywords.some(kw => command.toLowerCase().includes(kw));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);
    if (changes) {
      return {
        success: true,
        command: {
          type: 'property',
          changes,
          confidence: 0.9,
          description: `Updated time tracker: ${Object.keys(changes.properties || {}).join(', ')}`,
        },
        suggestions: [],
      };
    }
    return {
      success: false,
      command: { type: 'property', changes: {}, confidence: 0, description: 'Unrecognized time tracker command' },
      suggestions: [
        'switch to timesheet view',
        'add project "Website Redesign"',
        'set rate to $75',
        'add client "Acme Corp"',
        'add task "Development"',
        'set rounding to 15 minutes',
      ],
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'switch to timesheet view',
      'add project "Website Redesign"',
      'remove project "Old Project"',
      'set rate to $100',
      'add client "Acme Corp"',
      'add task "Code Review"',
      'set currency to EUR',
      'set rounding to 30 minutes',
      'set default to billable',
      'show tags',
    ];
  }

  async getSuggestions(): Promise<string[]> {
    return [
      'switch view mode', 'add project', 'set rate',
      'add client', 'add task', 'set currency',
    ];
  }
}

export const timeTrackerCommandHandler = new TimeTrackerCommandHandler();
